import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import {
  Alert, Avatar, Box, Button, CircularProgress, InputAdornment, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { DeleteOutline, ImageOutlined, SaveOutlined } from '@mui/icons-material';
import { Layout } from '../../components/common/Layout';
import { useBranding } from '../../contexts/BrandingContext';
import { BrandingConfig, brandingService } from '../../services/brandingService';
import { MedicalCheckupQueueSettingsPanel } from './MedicalCheckupQueueSettingsPanel';
import { NotificationSoundSettingsPanel } from './NotificationSoundSettingsPanel';
import { permissionService } from '../../services/permissionService';
import { RoleType } from '../../models/enums/roleType';

const getError = (error: any) => error?.response?.data?.message || error?.message || 'Не удалось сохранить настройки';

export const SettingsPage = () => {
  const isAdmin = permissionService.hasRole(RoleType.Admin);
  const isMedic = permissionService.hasRole(RoleType.Medic);
  const { branding, setBranding } = useBranding();
  const [form, setForm] = useState<BrandingConfig>(branding);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [logo, setLogo] = useState<File>();
  const [logoPreview, setLogoPreview] = useState(branding.logoUrl);
  const [removeLogo, setRemoveLogo] = useState(false);

  useEffect(() => {
    brandingService.get()
      .then(({ data }) => {
        if (data.success) {
          setForm(data.data);
          setLogoPreview(data.data.logoUrl);
          setBranding(data.data);
        }
      })
      .catch((e) => setError(getError(e)))
      .finally(() => setLoading(false));
  }, [setBranding]);

  useEffect(() => () => {
    if (logoPreview?.startsWith('blob:')) URL.revokeObjectURL(logoPreview);
  }, [logoPreview]);

  const update = (field: keyof BrandingConfig) => (event: ChangeEvent<HTMLInputElement>) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleLogo = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLogo(file);
    setLogoPreview(URL.createObjectURL(file));
    setRemoveLogo(false);
  };

  const clearLogo = () => {
    setLogo(undefined);
    setLogoPreview(undefined);
    setRemoveLogo(true);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess(false);

    const data = new FormData();
    data.append('ProductName', form.productName);
    data.append('OrganizationName', form.organizationName);
    data.append('PrimaryColor', form.primaryColor);
    if (logo) data.append('LogoFile', logo);
    data.append('RemoveLogo', String(removeLogo));

    try {
      const response = await brandingService.update(data);
      if (!response.data.success) throw new Error(response.data.message);
      setForm(response.data.data);
      setLogoPreview(response.data.data.logoUrl);
      setLogo(undefined);
      setRemoveLogo(false);
      setBranding(response.data.data);
      setSuccess(true);
    } catch (e) {
      setError(getError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title="Настройки">
      <Box>
        {isAdmin && <Paper component="form" onSubmit={handleSubmit} variant="outlined" sx={{ maxWidth: 920, p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6">Оформление личного кабинета</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>Настройки применяются ко всем пользователям вашей организации</Typography>

          {loading ? <Box py={8} textAlign="center"><CircularProgress /></Box> : (
            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Avatar src={logoPreview} variant="rounded" sx={{ width: 88, height: 88, bgcolor: 'primary.main' }}><ImageOutlined /></Avatar>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button component="label" variant="outlined" startIcon={<ImageOutlined />}>
                    Загрузить логотип
                    <input hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={handleLogo} />
                  </Button>
                  {logoPreview && <Button color="error" startIcon={<DeleteOutline />} onClick={clearLogo}>Удалить</Button>}
                </Stack>
              </Stack>

              <TextField required label="Название продукта" value={form.productName} onChange={update('productName')} inputProps={{ maxLength: 80 }} />
              <TextField required label="Название организации" value={form.organizationName} onChange={update('organizationName')} inputProps={{ maxLength: 160 }} />
              <TextField
                required label="Основной цвет" value={form.primaryColor} onChange={update('primaryColor')}
                inputProps={{ pattern: '^#[0-9A-Fa-f]{6}$', maxLength: 7 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Box component="input" type="color" aria-label="Выбрать основной цвет" value={/^#[0-9A-Fa-f]{6}$/.test(form.primaryColor) ? form.primaryColor : '#9588e8'} onChange={update('primaryColor')} sx={{ width: 32, height: 32, border: 0, p: 0, bgcolor: 'transparent', cursor: 'pointer' }} /></InputAdornment>,
                }}
                helperText="HEX-цвет, например #9588e8"
              />

              {error && <Alert severity="error">{error}</Alert>}
              {success && <Alert severity="success">Настройки сохранены и применены</Alert>}
              <Box><Button type="submit" variant="contained" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlined />} disabled={saving}>Сохранить</Button></Box>
            </Stack>
          )}
        </Paper>}
        {(isAdmin || isMedic) && <NotificationSoundSettingsPanel />}
        {isAdmin && <MedicalCheckupQueueSettingsPanel />}
      </Box>
    </Layout>
  );
};
